import {Factory} from '../Factory';
import {TokenService} from '../authentication/TokenService';
import {IConfigService} from '../config';
import {StateService} from '../state/StateService';
import {INotificationService} from '../notification';
import {Client} from '@stomp/stompjs'

import IIntervalService = angular.IIntervalService;

export class WebSocketService {
  private isConnected: boolean;
  private connectionId: number = 0;
  private currentProject: any = null;

  private readonly accountSubscription: any[];
  private readonly projectSubscriptions: any[];
  private readonly roleSubscriptions: any[];
  private readonly client: Client;

  constructor(private rootScope: IRootScope, private intervalService: IIntervalService, private tokenService: TokenService, private configService: IConfigService, private stateService: StateService, private notificationService: INotificationService) {
    this.accountSubscription = [];
    this.projectSubscriptions = [];
    this.roleSubscriptions = [];
    this.isConnected = false;

    this.client = new Client({
      reconnectDelay: 5000,
      heartbeatIncoming: 5000,
      heartbeatOutgoing: 0,
      beforeConnect: () => {
        const token = this.tokenService.GetAccessToken();
        this.client.brokerURL = `${this.configService.getServerBaseUrl()}/websocket?access_token=${token}`.replace("http://", "ws://").replace("https://", "wss://");
      },
    });

    // isConnected must track the real STOMP session: activate() is async, so the
    // flag is only set true inside onConnect and cleared on any drop. onConnect
    // also re-subscribes (the broker does not auto-resubscribe after reconnect).
    this.client.onConnect = () => {
      this.isConnected = true;
      console.log(`[WS conn=${this.connectionId}] Connected`);
      this.SubscribeToAccount();
      if (this.currentProject) {
        this.SubscribeToProject(this.currentProject);
      }
    };

    this.client.onStompError = c => {
      this.isConnected = false;
      console.error(`[WS conn=${this.connectionId}] Broker STOMP error:`, c.headers);
    };

    this.client.onDisconnect = () => {
      this.isConnected = false;
      this.discardSubscriptions();
      console.warn(`[WS conn=${this.connectionId}] STOMP disconnected`);
    };

    this.client.onWebSocketError = e => {
      console.error(`[WS conn=${this.connectionId}] WebSocket error:`, e);
    };

    this.client.onWebSocketClose = e => {
      this.isConnected = false;
      this.discardSubscriptions();
      console.warn(`[WS conn=${this.connectionId}] WebSocket closed — code=${e.code} reason="${e.reason}" wasClean=${e.wasClean}`);
    };

    rootScope.$on(EVENTS.LOGIN_REQUIRED, () => this.UnSubscribeFromAll());
    rootScope.$on(EVENTS.LOGIN_CONFIRMED, () => this.ConnectAndSubscribeToAccount());
    rootScope.$on("ProjectService.CurrentProjectChanged", (event, project) => {
      // Re-subscribing costs an UNSUBSCRIBE/SUBSCRIBE round trip per topic, and this
      // event now fires from React whenever the cached project object is replaced.
      // Only the project identity matters to the broker, so ignore same-id repeats.
      const sameProject = this.currentProject?.id && project?.id === this.currentProject.id;
      this.currentProject = project;
      if (sameProject && this.projectSubscriptions.length) return;
      this.SubscribeToProject(project);
    });
  }

  // Job-init events are typed "<springProfile>Init", and the spring profile carries the
  // environment: "processorInit" (prod), "staging-processorInit" / "dev-processorInit"
  // (prefixed), "processor-us-gov-east-1Init" (regional prod, suffixed). Listeners only
  // care about the job kind, so a normalised alias is broadcast alongside the raw type —
  // $rootScope.$on matches names exactly, so a listener cannot pattern-match this itself.
  private static readonly JOB_KINDS = ['processor', 'exporter', 'tiffer'];

  private static normalizeJobInitType(type: string): string | null {
    if (typeof type !== 'string' || !type.endsWith('Init')) return null;

    const profile = type.slice(0, -'Init'.length);
    const kind = WebSocketService.JOB_KINDS.find(
      k => profile.startsWith(`${k}-`) || profile.endsWith(`-${k}`)
    );

    return kind ? `${kind}Init` : null;
  }

  private static unsubscribeFromGroup(group) {
    group.forEach((sub) => {
      try {
        sub.unsubscribe();
      } catch (e) {
        // Subscription may belong to a closed session; the broker already
        // dropped it, so a failed UNSUBSCRIBE on a dead socket is harmless.
      }
    });
    group.length = 0;
  }

  // Socket dropped: the broker discards every subscription, so just drop our
  // stale references. Unsubscribing here would transmit on a closed socket.
  private discardSubscriptions() {
    this.accountSubscription.length = 0;
    this.projectSubscriptions.length = 0;
    this.roleSubscriptions.length = 0;
  }

  public ConnectAndSubscribeToAccount() {
    console.log("Subscribing...");
    this.connect();
  }

  public SubscribeToAccount() {
    this.waitForConnection().then(_ => {
      WebSocketService.unsubscribeFromGroup(this.accountSubscription);
      this.accountSubscription.push(
        this.client.subscribe("/user/exchange/amq.direct/message", message => this.handleMessage(JSON.parse(message.body), message.headers))
      );
    });
  }

  public SubscribeToProject(project) {
    if (project) {
      this.waitForConnection().then(() => {

        console.log("Subscribing to project", {id: project?.id, name: project?.name});
        WebSocketService.unsubscribeFromGroup(this.projectSubscriptions);
        this.projectSubscriptions.push(
          this.client.subscribe("/topic/project." + project.id, message => this.handleMessage(JSON.parse(message.body), message.headers, project.id))
        );

        // I know this does a foreach, but you really only get one role from a project.
        project.roles.forEach((role) => {
          this.SubscribeToRole(role);
        })

      });
    } else {
      WebSocketService.unsubscribeFromGroup(this.projectSubscriptions);
      WebSocketService.unsubscribeFromGroup(this.roleSubscriptions);
    }
  }

  public SubscribeToRole(role) {
    if (role) {
      this.waitForConnection().then(() => {

        console.log("Subscribing to role", role);
        WebSocketService.unsubscribeFromGroup(this.roleSubscriptions);
        this.roleSubscriptions.push(
          this.client.subscribe("/topic/role." + role.id, message => this.handleMessage(JSON.parse(message.body), message.headers))
        );

      });
    }
  }

  public UnSubscribeFromAll() {
    console.log("Unsubscribing...");
    this.currentProject = null;
    if (this.isConnected) {
      WebSocketService.unsubscribeFromGroup(this.accountSubscription);
      WebSocketService.unsubscribeFromGroup(this.projectSubscriptions);
      WebSocketService.unsubscribeFromGroup(this.roleSubscriptions);

      this.isConnected = false;

      this.client.deactivate({force: true}).then(() => {
        console.log("Disconnected");
      });
    }
  }

  private handleMessage(payload, headers, projectId?: string) {
    this.rootScope.$apply(() => {
      if (payload.type === 'notification') {
        this.notificationService.addCurrentMessage(payload.message);
        // Notifications are the only signal for some completions (collection processing
        // finishing emits no ProcessingEnd), so relay them to React as well as the toast.
        // The envelope carries projectId; the message itself does not.
        const notification = {...(payload.message ?? {})};
        const notificationProjectId = payload.projectId ?? projectId;
        if (notificationProjectId && !notification.projectId) {
          notification.projectId = notificationProjectId;
        }
        this.rootScope.$broadcast('FeedService.Notification', notification);
      } else {
        const message = payload.message ?? {};
        if (message && typeof message === 'object' && !message.projectId && projectId) {
          message.projectId = projectId;
        }
        this.rootScope.$broadcast('FeedService.' + payload.type, message);

        // e.g. staging-processorInit also fires FeedService.processorInit.
        const alias = WebSocketService.normalizeJobInitType(payload.type);
        if (alias) {
          this.rootScope.$broadcast('FeedService.' + alias, message);
        }
      }
    });
  };

  private connect() {
    try {
      ++this.connectionId;
      console.log(`[WS conn=${this.connectionId}] Activating`);
      this.client.activate();
    } catch (e) {
      this.isConnected = false;
      throw e;
    }
  }

  private async waitForConnection() {
    return new Promise((resolve => {
      const stop = this.intervalService(() => {
        if (this.isConnected) {
          this.intervalService.cancel(stop);
          resolve(true);
        }
      }, 300);
    }));
  }
}

frontendApp.factory("WebSocketService", ["$rootScope", "$interval", "TokenService", "ConfigService", "StateService", "NotificationService", Factory.Create(WebSocketService)]);
